/**
 * These pages cover three distinct route conditions:
 *  - Generic article/news lists (/articles, /news)
 *  - Category pages for either (/articles/category/hiking, /news/category/covid-19)
 *  - Place-scoped pages (/brazil/articles)
 */

import PropTypes from "prop-types";

import { articlePageAPI } from "@queries/article/list/api";
import { NoResults } from "@components/noResults";
import { Pageblock } from "@components/pageblock";
import { Pagination } from "@components/pagination";
import { Ad } from "@components/ad";
import { Hero3up } from "@components/hero3up";
import { BasicListCard } from "@components/cards/basicList";

import { buildArticleMeta } from "@utils/buildArticleMeta";
import { LP_URL } from "@constants/lpUrl";
import { NextHead } from "@components/nextHead";

export default function ArticleSearchPage({
  items = [],
  placeName = "",
  category = "",
  hero,
  url,
  canonicalSlug,
  type,
  numPages,
  page,
  count = 0,
}) {
  const primaryCategory = category.replace(/-/g, " ");
  const makeHeading = (c, t) => {
    if (c) return c;
    const kind = t === "feature" ? "Stories" : "News";
    return `Travel ${kind}`;
  };

  const meta = buildArticleMeta(type, placeName, primaryCategory);

  const capitalizeFirst = catString => {
    if (catString) {
      const categorySplit = catString ? catString.split(" ") : "";
      return categorySplit
        .map(cat => {
          if (cat === "and") {
            return cat;
          }
          return cat[0].toUpperCase() + cat.substring(1);
        })
        .join("  ");
    }
    return catString;
  };

  return (
    <>
      <NextHead>
        <title>{meta.title}</title>
        <meta key="title" name="title" content={meta.title} />
        <meta key="og:title" property="og:title" content={meta.title} />
        <meta key="twitter:title" name="twitter:title" content={meta.title} />
        <meta key="description" name="description" content={meta.desc} />
        <meta
          key="og:description"
          property="og:description"
          content={meta.desc}
        />
        <meta
          key="twitter:description"
          name="twitter:description"
          content={meta.desc}
        />
        {/* If the URL has query params, use the URL with query params as canonical url so Google can crawl paginated pages */}
        <link
          key="canonical"
          rel="canonical"
          href={`${LP_URL}${canonicalSlug}`}
        />
        <meta
          key="og:url"
          property="og:url"
          content={`${LP_URL}${canonicalSlug}`}
        />
      </NextHead>

      <Hero3up
        title={makeHeading(
          primaryCategory === "Beaches coasts and islands"
            ? " Beaches, Coasts and Islands"
            : capitalizeFirst(primaryCategory),
          type,
          placeName
        )}
        subheading={placeName}
        data={hero}
      />

      <Pageblock className="container">
        <div className="lg:flex lg:space-x-24">
          <div className="flex-grow">
            {Array.isArray(items) && items.length > 0 && (
              <div id="article-search-results">
                <ul>
                  {items.map(
                    (
                      {
                        esid,
                        title,
                        excerpt,
                        href,
                        featuredImage,
                        tags,
                        subhead,
                      },
                      index
                    ) => {
                      const cats =
                        Array.isArray(tags) && tags.length > 0
                          ? tags.slice(0, 2).map(cat => ({
                              url: `/${
                                type === "feature" ? "articles" : "news"
                              }/category/${cat}`,
                              label: cat.replace(/-/g, " "),
                            }))
                          : [];

                      return (
                        <li key={esid}>
                          {/**
                           * At the following intervals, place an ad
                           */}
                          {[2, 9, 17].includes(index) && (
                            <Ad
                              placement="native"
                              path="articles/native-content-articles-list"
                              className="my-6"
                            />
                          )}

                          <div>
                            <BasicListCard
                              image={featuredImage}
                              eyebrow={cats[0]?.label}
                              title={title}
                              subhead={subhead}
                              description={{ hideMobile: true, text: excerpt }}
                              href={href}
                              titleClamp={3}
                            />
                          </div>
                        </li>
                      );
                    }
                  )}
                </ul>
              </div>
            )}

            {count === 0 && (
              <NoResults className="mt-8" query="" error={new Error()} />
            )}

            {numPages > 1 && (
              <Pagination
                selectedPage={Number(page)}
                numPages={numPages > 100 ? 100 : numPages}
                className="mt-4"
                formSlug={url}
              />
            )}
          </div>

          <aside className="lg:w-1/3 flex-none">
            <Ad
              framed
              path="articles/all-articles-right-rail"
              placement="railShort"
              additionalTargeting={{
                position: "right-rail",
              }}
            />

            <div className="sticky top-8 mt-8">
              <Ad
                framed
                path="articles/right-rail"
                placement="sidebar"
                additionalTargeting={{
                  position: "right-rail",
                }}
              />
            </div>
          </aside>
        </div>
      </Pageblock>

      <Ad
        framed
        placement="superzone"
        path="articles/superzone"
        additionalTargeting={{
          position: "superzone",
        }}
      />
    </>
  );
}
/**
 * Handles the URL for client and server transitions and returns the pathname
 * @param {*} req
 * @returns `/:placeSlug(*)/:type(article|news)/category/:category`
 */
const getBaseUrl = req => {
  /**
   * On client side transitions a `.json` path is returned as the URL value
   * when this occurs the `referer` header has the expected `pathname`
   *
   * Read more here:
   * https://nextjs.org/docs/pages/building-your-application/data-fetching/get-server-side-props#when-does-getserversideprops-run
   */
  const slug = req.url.includes(".json")
    ? new URL(req.headers.referer).pathname
    : new URL(`http://_${req.url}`).pathname; // protocol included to avoide invalid URL error

  return slug;
};

export async function getServerSideProps({ query, req, res }) {
  res.setHeader("Cache-Control", "no-cache, max-age=120, must-revalidate");

  const { page = 1 } = query;
  const url = getBaseUrl(req);

  const articleTypes = {
    articles: "feature",
    news: "news",
  };
  const pathType = url.includes("news") ? "news" : "articles";
  const type = articleTypes[pathType];
  /**
   * Check for :placeSlug* in URL - cannot rely on query.placeSlug
   * since that value can change between server and client side transitions
   * - split string on `/:articleType(articles|news)` to isolate `:placeSlug*`
   * - split `/:placeSlug*` on `/` to return an array of slugs
   * - remove the first item in the array since it will be an empty string
   */
  const urlPlaceSlug = url.split(`/${pathType}`)?.[0].split("/").slice(1);
  // also check for :category in URL using similar method
  const urlCategorySlug =
    url.split(`/${pathType}/category/`).slice(1)?.[0] || "";

  const filters = {
    type,
    placeSlug: urlPlaceSlug,
    ...(urlCategorySlug ? { tag: urlCategorySlug } : {}),
  };
  const pageSize = 25;
  const options = {
    page,
    pageSize,
  };

  const data = await articlePageAPI({ filters, options, url });

  /**
   * Check that GQL has returned data for this article list, record exists
   */
  if (!data) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      ...data,
      type,
      url,
      category: urlCategorySlug,
      page,
      numPages: Math.floor(data.count / pageSize),
    },
  };
}

ArticleSearchPage.propTypes = {
  hero: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  category: PropTypes.string,
  url: PropTypes.string.isRequired,
  canonicalSlug: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({})),
  numPages: PropTypes.number.isRequired,
  page: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  placeName: PropTypes.string,
};
